import { createSwitchlessReducer } from '../../util/switchlessReducer';
import namespace from './namespace';
import type { Dispatch } from 'redux';
import fulfill from '../../fulfill';
import { CHECKOUT_API_HOSTNAME } from '../../env';
import { Transaction, Session } from '../../types/checkout';
import { createAccountIdChangedCaseReducer } from '../../auth/useAccountId';
import { Error } from '../../types/checkout/generated';
import { getLastSessionEventName } from '../sessionHelpers';
import { sleep } from '../../helpers/sleep';
import { ActionTypes, SwapSession } from '../sessions/actions';

export enum CaptureStates {
    None = 'None',
    Captured = 'Captured',
    Failed = 'Failed',
}

export interface State {
    isLoading: boolean;
    session: Session | undefined;
    captureState: CaptureStates;
    sessionId: string | undefined;
}

export const initialState: State = {
    isLoading: false,
    session: undefined,
    captureState: CaptureStates.None,
    sessionId: undefined,
};

type Requested = { account_id: string; session_id: string };
type Received = Requested & { session: Session };

export const { reducer, actions } = createSwitchlessReducer({
    namespace: namespace,
    initialState,
    reducers: {
        startLoading: (state: State, { session_id }: Requested) => {
            if (session_id === state?.session?.id) {
                return state;
            }
            return initialState;
        },
        setSession: (state: State, { session }: Received): State => {
            return {
                ...state,
                isLoading: false,
                session,
            };
        },
        setCaptureState: (state: State, { captureState }: {captureState: CaptureStates}) => {
            return {
                ...state,
                captureState,
            };
        },
        setError: (state: State, { error }: {error: Error | undefined}) => {
            return {
                ...state,
                error,
                isLoading: false,
            };
        },
    },
    globalReducer: createAccountIdChangedCaseReducer(() => initialState),

});

const getTransaction = async (accountId: string, transactionId: string): Promise<Transaction> => {
    return await fulfill.get({
        accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/transactions/${transactionId}`,
        handlers: {
            200: (transaction: Transaction) => transaction,
        },
    });
};

const getCaptureState = (transaction: Transaction) => {
    const reversedEvents = [...transaction.events].reverse();
    const lastCapture = reversedEvents.find((event) => event.event === 'CAPTURE');
    if (lastCapture && lastCapture.success) {
        return CaptureStates.Captured;
    } else if (lastCapture && !lastCapture.success) {
        return CaptureStates.Failed;
    }
    return CaptureStates.None;
};

const checkTransaction = async (accountId: string, transactionId: string) => {
    const transaction = await getTransaction(accountId, transactionId);
    return getCaptureState(transaction);
};

const getCapturedState = async (account_id: string, session: Session) => {
    const lastSessionEvent = getLastSessionEventName(session);
    if (session.transaction_id && lastSessionEvent === 'AUTHORIZED' && session.configuration.auto_capture) {
        // check transaction immediately, if no capture operation then wait 15 seconds and check again once
        let captureResult = await checkTransaction(account_id, session.transaction_id);
        if (captureResult === CaptureStates.None) {
            await sleep(15 * 1000);
            captureResult = await checkTransaction(account_id, session.transaction_id);
        }
        return captureResult;
    }
    return CaptureStates.None;
};

export const createSessionActions = (dispatch: Dispatch) => {
    const fetchSession = async (account_id: string, session_id: string) => {
        dispatch(actions.startLoading({ account_id, session_id }));
        const session = await fulfill.get({
            accountId: account_id,
            url: `${CHECKOUT_API_HOSTNAME}/v1/sessions/${session_id}?includes=events.request_headers&includes=initiating_system_request_headers`,
            handlers: {
                200: (session: Session) => {
                    dispatch(actions.setSession({ account_id, session_id, session }));
                    dispatch(swapSession(session));
                    return session;
                },
            },
        }) as Session | undefined;
        if (session) {
            const captureState = await getCapturedState(account_id, session);
            dispatch(actions.setCaptureState({ captureState }));
        }
    };

    const closeErrorModal = () => {
        dispatch(actions.setError({ error: undefined }));
    };

    return {
        fetchSession,
        closeErrorModal,
    };
};


/* Legacy side effect support to replace the session in the list with the most recent response from checkout-service */
const swapSession = (session: Session): SwapSession => ({
    type: ActionTypes.SwapSession,
    payload: {
        session,
    },
});
